// @ts-nocheck
import React from 'react';
import { 
  Box, 
  FormControl, 
  IconButton, 
  InputLabel, 
  MenuItem, 
  Select, 
  Slider, 
  Typography 
} from '@mui/material';
import { 
  FaPlay, 
  FaPause, 
  FaStepForward, 
  FaUndo, 
  FaTags 
} from 'react-icons/fa';

interface ProcessedModelDetail {
  routeShort: string;
  label?: string;
}

interface ChannelOption {
  value: string;
  label: string;
  disabled?: boolean;
  variationChannel?: string;
  maxValue?: number;
}

interface VariationOption {
  value: string;
  label: string;
}

interface ForecastControlsProps {
  // Display state
  isMobile: boolean;
  showAllTooltips: boolean;
  setShowAllTooltips: (show: boolean) => void;

  // Forecast selection
  forecastType: string;
  handleForecastTypeChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  showWindPowerOption: boolean;

  // Channel and variation controls
  forecastChannel: string;
  handleForecastChannelChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  channelOptions: ChannelOption[];

  forecastVariation: string;
  handleForecastVariationChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  variationOptions: VariationOption[];

  // Time slider
  timeRange: any[];
  loading: boolean;
  sliderValue: number;
  handleSliderChange: (event: Event, newValue: number | number[]) => void;
  formatSliderTime: () => string;

  // Animation controls
  isPlaying: boolean;
  togglePlayAnimation: () => void;
  restartSlider: () => void;
  stepForward: () => void;

  // Model data
  processedModelDetails: ProcessedModelDetail[];
  dataFetched: boolean;
  assetItems: any[];

  // Filtering
  assetGroup?: any;
  filterForecastTypes: (forecastTypeKey: string) => boolean;
}

/**
 * A component that provides controls for configuring the forecast display
 * including forecast type selection, channel/variation options, and time controls
 */
const ForecastControls: React.FC<ForecastControlsProps> = ({
  isMobile,
  showAllTooltips,
  setShowAllTooltips,
  forecastType,
  handleForecastTypeChange,
  showWindPowerOption,
  forecastChannel,
  handleForecastChannelChange,
  channelOptions,
  forecastVariation,
  handleForecastVariationChange,
  variationOptions,
  timeRange,
  loading,
  sliderValue,
  handleSliderChange,
  formatSliderTime,
  isPlaying,
  togglePlayAnimation,
  restartSlider,
  stepForward,
  processedModelDetails,
  dataFetched,
  assetItems,
  assetGroup,
  filterForecastTypes
}) => {
  if (isMobile) return null;
  
  return (
    <div className="w-full bg-white p-3 flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-4">
      <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
        <div className="flex items-center gap-1">
          <IconButton
            size="small"
            onClick={() => setShowAllTooltips(!showAllTooltips)}
            color="default"
            title={showAllTooltips ? "Hide tooltips" : "Show all tooltips"}
            sx={{
              border: showAllTooltips ? '1px solid #879643' : '1px solid #d0d0d0',
              backgroundColor: showAllTooltips ? "#879643" : 'white',
              '&:hover': { backgroundColor: showAllTooltips ? 'rgba(135, 150, 67, 0.8)' : 'rgba(0, 0, 0, 0.04)' },
              marginRight: '8px'
            }}
          >
            <FaTags style={{ color: showAllTooltips ? 'white' : '#333333' }} />
          </IconButton>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Forecast Type</InputLabel>
            <Select
              value={forecastType}
              onChange={handleForecastTypeChange}
              label="Forecast Type"
            >
              {/* Processed Models section */}
              {/* Erode AI Forecasts header */}
              {processedModelDetails && processedModelDetails.length > 0 && 
                <MenuItem disabled sx={{ fontWeight: 'bold', color: '#082b61', mt: 1 }}>— Erode AI Forecasts —</MenuItem>
              }
              
              {/* Processed model options */}
              {processedModelDetails && 
                // 1) Filter to one item per unique routeShort
                processedModelDetails.filter((model, idx, all) =>
                  all.findIndex(m => m.routeShort === model.routeShort) === idx
                )
                // 2) Map each unique model into a <MenuItem>
                .map(model => {
                  const key = model.routeShort;
                  const hasProcessedModel = assetItems.some(asset =>
                    asset?.processedModels?.items?.some(pm => pm.routeShort === key)
                  );
                  return (
                    <MenuItem
                      key={`processed-${key}`}
                      value={`processed-${key}`}
                      disabled={!hasProcessedModel}
                      sx={!hasProcessedModel ? { color: 'rgba(0, 0, 0, 0.38)' } : {}}
                    >
                      {model.label || key}
                    </MenuItem>
                  );
                })}
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#082b61' }}>— Forecast Types —</MenuItem>
              {filterForecastTypes('precipitation') && <MenuItem value="precipitation">Precipitation</MenuItem>}
              {filterForecastTypes('wind10m') && <MenuItem value="wind10m">Wind Speed (10m)</MenuItem>}
              {filterForecastTypes('wind80m') && <MenuItem value="wind80m">Wind Speed (80m)</MenuItem>}
              {showWindPowerOption && filterForecastTypes('windPower') && <MenuItem value="windPower">Wind Power (MW)</MenuItem>}
              {filterForecastTypes('temperature') && <MenuItem value="temperature">Temperature</MenuItem>}
              {filterForecastTypes('humidity') && <MenuItem value="humidity">Humidity</MenuItem>}
              {filterForecastTypes('solarS') && <MenuItem value="solarS">Solar Radiation (Short)</MenuItem>}
              {filterForecastTypes('solarL') && <MenuItem value="solarL">Solar Radiation (Long)</MenuItem>}
              {filterForecastTypes('soilMoisture') && <MenuItem value="soilMoisture">Soil Moisture</MenuItem>}
              {filterForecastTypes('snowWater') && <MenuItem value="snowWater">Snow Water Equivalent</MenuItem>}

            </Select>
          </FormControl>
        </div>

        {/* Channel dropdown */}
        {forecastType && channelOptions.length > 0 && (
          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={forecastChannel}
              onChange={handleForecastChannelChange}
              label="Channel"
            >
              {channelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Variation dropdown - not shown for processed models */}
        {variationOptions.length > 0  && !forecastType.startsWith('processed-') && (
          <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Variation</InputLabel>
            <Select
              value={forecastVariation}
              onChange={handleForecastVariationChange}
              label="Variation"
            >
              {variationOptions.map((variation) => (
                <MenuItem key={variation.value} value={variation.value}>
                  {variation.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: 'center' }}>
            {formatSliderTime()}
          </Typography>
          <IconButton
            size="small"
            onClick={restartSlider}
            disabled={timeRange.length === 0 || loading || sliderValue === 0}
            color="default"
            sx={{ ml: 1 }}
            title="Restart"
          >
            <FaUndo style={{ color: (timeRange.length === 0 || loading || sliderValue === 0) ? undefined : "#879643" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={togglePlayAnimation}
            disabled={timeRange.length === 0 || loading || (sliderValue >= timeRange.length - 1)}
            color="default"
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? 
              <FaPause style={{ color: (timeRange.length === 0 || loading || (sliderValue >= timeRange.length - 1)) ? undefined : "#879643" }} /> : 
              <FaPlay style={{ color: (timeRange.length === 0 || loading || (sliderValue >= timeRange.length - 1)) ? undefined : "#879643" }} />
            }
          </IconButton>
          <IconButton
            size="small"
            onClick={stepForward}
            disabled={timeRange.length === 0 || loading || isPlaying || (sliderValue >= timeRange.length - 1)}
            color="default"
            title="Step Forward"
          >
            <FaStepForward style={{ color: (timeRange.length === 0 || loading || isPlaying || (sliderValue >= timeRange.length - 1)) ? undefined : "#879643" }} />
          </IconButton>
        </Box>
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          min={0}
          max={timeRange.length > 0 ? timeRange.length - 1 : 0}
          disabled={timeRange.length === 0 || loading}
          valueLabelDisplay="auto"
          valueLabelFormat={formatSliderTime}
          size="small"
          sx={{
            '& .MuiSlider-thumb': {
              backgroundColor: '#879643',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0px 0px 0px 8px rgba(135, 150, 67, 0.16)'
              }
            },
            '& .MuiSlider-track': {
              backgroundColor: '#879643'
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#E0E0E0'
            }
          }}
        />
      </Box>
    </div>
  );
};

export default ForecastControls;